import React from "react";
import { Text, View, TouchableHighlight } from "react-native";
import { EStyleSheet } from "react-native-extended-stylesheet-typescript";
import { SubmitPicks, PlatformOutcome } from "@freeplay/shared-code";
import { GlobalStyles, Transition } from "../style/Style";
import { useServices } from "../../contexts/ServiceContext";
import { MediaQueries } from "../hooks/dynamicStyleHook";
import { TeamMap } from "../teams/teams";

export interface PropProps {
    teamName: string;
    propString: string;
    isOverUnder: boolean;
    isSelected: boolean;
    roundLeft: boolean;
    index: number;
    platformEventId: number;
    platformOutcome: PlatformOutcome;
    disabled?: boolean;
    onSelect: (picks: SubmitPicks, index: number) => void;
}

export const Prop: React.FC<PropProps> = ({
    teamName,
    propString,
    isOverUnder,
    isSelected,
    roundLeft,
    index,
    platformEventId,
    platformOutcome,
    disabled,
    onSelect,
}) => {
    const { edgeDeploy } = useServices();
    const styles = GenerateSelectionContainerStyles(isSelected, roundLeft);

    let propStyle;
    let textColourStyle;
    if (isOverUnder) {
        propStyle = [styles.overUnder];
    } else {
        propStyle = isSelected
            ? [{ backgroundColor: EStyleSheet.value(GlobalStyles.PROP_SELECTED_BACKGROUND) }]
            : [{ backgroundColor: TeamMap.get(teamName) }];

        textColourStyle = isSelected
            ? [{ color: EStyleSheet.value(GlobalStyles.PROP_SELECTED_TEXT) }]
            : [{ color: EStyleSheet.value(GlobalStyles.PROP_TEAM_TEXT) }];
    }

    return (
        <View style={[styles.prop, edgeDeploy ? styles.edgeProp : {}, propStyle, Transition()]}>
            <TouchableHighlight
                underlayColor="none"
                style={[styles.innerOutline, edgeDeploy ? styles.edgeInnerOutline : {}, Transition()]}
                disabled={disabled}
                onPress={() => {
                    onSelect(
                        {
                            platformEventId: platformEventId,
                            platformOfferId: platformOutcome.platformOffer,
                            platformOutcomeId: platformOutcome.id,
                        },
                        index,
                    );
                }}
            >
                <View style={styles.infoContainer}>
                    <Text style={[styles.team, textColourStyle]}>{`${
                        teamName === "WAS Football Team" ? "WAS Football" : teamName
                    }`}</Text>
                    <Text style={[styles.odd, textColourStyle]}>{propString}</Text>
                </View>
            </TouchableHighlight>
        </View>
    );
};

const GenerateSelectionContainerStyles = (isSelected: boolean, roundLeft: boolean) =>
    EStyleSheet.create({
        [MediaQueries.SMALL]: {
            edgeProp: {
                height: 44,
                borderBottomLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
                borderTopLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
                borderTopRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
                borderBottomRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            },
            edgeInnerOutline: {
                borderBottomLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
                borderTopLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
                borderTopRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
                borderBottomRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            },
        },
        prop: {
            flex: 1,
            height: 48,
            borderBottomLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
            borderTopLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
            borderTopRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            borderBottomRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            justifyContent: "center",
            alignItems: "center",
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderColor: isSelected ? () => EStyleSheet.value(GlobalStyles.PROP_SELECTED) : "#ffffff",
        },
        edgeProp: {
            height: 60,
            borderBottomLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
            borderTopLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
            borderTopRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            borderBottomRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
        },
        innerOutline: {
            flex: 1,
            borderBottomLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
            borderTopLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
            borderTopRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            borderBottomRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            paddingLeft: roundLeft ? 0 : 15,
            paddingRight: roundLeft ? 15 : 0,
            width: "100%",
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderColor: isSelected ? () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR) : "transparent",
            textAlign: "center",
        },
        edgeInnerOutline: {
            borderBottomLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
            borderTopLeftRadius: roundLeft ? () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS) : 0,
            borderTopRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
            borderBottomRightRadius: roundLeft ? 0 : () => EStyleSheet.value(GlobalStyles.PROP_BORDER_RADIUS),
        },
        infoContainer: {
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            alignContent: "center",
            paddingTop: 2,
        },
        overUnder: {
            backgroundColor: isSelected
                ? () => EStyleSheet.value(GlobalStyles.PROP_SELECTED_BACKGROUND)
                : () => EStyleSheet.value(GlobalStyles.PROP_BACKGROUND),
        },
        team: {
            color: isSelected
                ? () => EStyleSheet.value(GlobalStyles.PROP_SELECTED_TEXT)
                : () => EStyleSheet.value(GlobalStyles.PROP_TEXT),
            fontSize: 18,
            lineHeight: 18,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
        },
        odd: {
            color: isSelected
                ? () => EStyleSheet.value(GlobalStyles.PROP_SELECTED_TEXT)
                : () => EStyleSheet.value(GlobalStyles.PROP_TEXT),
            fontSize: 16,
            lineHeight: 16,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
        },
    });
